import React, { useMemo } from 'react';
import { Copy } from 'lucide-react';
import Select from './Select';
import { DAYS, TIME_OPTIONS, describeTimings, timingsProblem } from '../utils/doctorTimings';

// One person's weekly working hours: tick the days they work and set from/to times for each.
//
// ONE set of hours per person (owner, 2026-10-05). Attendance judges lateness, a full day and
// absence against these, and for a doctor the same hours are what patients are shown on the
// prescription, the token slip and the waiting-room board. They used to be two separate records
// edited on two different screens.
//
// Inline and controlled, not a dialog: it sits inside the Add/Edit User form, which is already a
// modal, and a dialog inside a dialog is a trap on a phone.

const DEFAULT_SLOT = { from: '09:00', to: '17:00' };

const WorkingHoursEditor = ({ value, onChange, disabled = false, forPatients = false }) => {
  const byDay = value || {};

  const toggleDay = (key) => {
    if (byDay[key]) {
      const { [key]: _removed, ...rest } = byDay;
      onChange(rest);
      return;
    }
    // A newly ticked day starts with the hours of the closest day already set before it in the
    // week (so ticking Tue after Mon copies Monday), or the hospital's usual day.
    const idx = DAYS.findIndex((d) => d.key === key);
    const before = [...DAYS.slice(0, idx)].reverse().find((d) => byDay[d.key]);
    const after = DAYS.slice(idx + 1).find((d) => byDay[d.key]);
    const source = (before && byDay[before.key]) || (after && byDay[after.key]) || DEFAULT_SLOT;
    onChange({ ...byDay, [key]: { ...source } });
  };

  const setTime = (key, field, v) => onChange({ ...byDay, [key]: { ...byDay[key], [field]: v } });

  const firstSet = DAYS.find((d) => byDay[d.key]);
  const copyToAll = () => {
    if (!firstSet) return;
    const src = byDay[firstSet.key];
    onChange(Object.fromEntries(Object.keys(byDay).map((k) => [k, { ...src }])));
  };

  const problem = timingsProblem(byDay);
  const summary = useMemo(() => describeTimings(byDay), [byDay]);
  const anyDay = Object.keys(byDay).length > 0;

  return (
    <div>
      <p className="text-sm text-gray-600 mb-3">
        Tick the days this person works and set the hours. Attendance counts a late arrival and a
        full day against them.
        {forPatients && ' For a doctor they are also printed on the prescription and the token slip, and shown on the Display Board.'}
      </p>

      <div className="space-y-2">
        {DAYS.map((d) => {
          const t = byDay[d.key];
          return (
            <div
              key={d.key}
              className={`flex flex-wrap items-center gap-3 rounded-xl border px-3 py-2 ${t ? 'border-navy-200 bg-navy-50/50' : 'border-gray-200'}`}
            >
              <label className="flex items-center gap-2 w-32 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={Boolean(t)}
                  onChange={() => toggleDay(d.key)}
                  disabled={disabled}
                  className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                />
                <span className={`font-medium ${t ? 'text-gray-900' : 'text-gray-500'}`}>{d.label}</span>
              </label>
              {t ? (
                <div className="flex items-center gap-2">
                  <Select value={t.from} onChange={(v) => setTime(d.key, 'from', v)} options={TIME_OPTIONS} disabled={disabled} className="w-32" buttonClassName="p-2 border-2 rounded-lg" />
                  <span className="text-gray-400 text-sm">to</span>
                  <Select value={t.to} onChange={(v) => setTime(d.key, 'to', v)} options={TIME_OPTIONS} disabled={disabled} className="w-32" buttonClassName="p-2 border-2 rounded-lg" />
                </div>
              ) : (
                <span className="text-sm text-gray-400">Off</span>
              )}
            </div>
          );
        })}
      </div>

      {Object.keys(byDay).length > 1 && (
        <button
          type="button"
          onClick={copyToAll}
          disabled={disabled}
          className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-navy-700 hover:text-navy-900 disabled:opacity-50"
        >
          <Copy className="h-4 w-4" /> Copy {firstSet?.label}&rsquo;s hours to all ticked days
        </button>
      )}

      <div className="mt-3 rounded-xl bg-gray-50 border border-gray-200 px-4 py-3 text-sm">
        <span className="font-medium text-gray-700">Reads as: </span>
        <span className="text-gray-900">
          {anyDay ? summary : 'No hours set, so they follow the hospital hours'}
        </span>
      </div>
      {problem && <p className="mt-2 text-sm text-red-600">{problem}</p>}
    </div>
  );
};

export default WorkingHoursEditor;
